import { theme } from '@features/executive-report/domain/theme';
import { Slide } from '@features/powerpoint';
import {
  chartColors,
  defaultChartOptions,
} from '../demographic-profile/default-chart-options';
import { NameValuePair } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { chartBackgroundOptions } from './chart-background-options';
import { applyChartXOffset, applyChartYOffset } from './layout';
import { chartTitleText } from './chart-title-text';

const titleColors = [
  theme.colors.lightBlue,
  theme.colors.inmarketGreen,
] as const;

const highlightColors = ['#4E71BE', '#7EAC55'] as const;

const colorByIndex = (index: number, colors: readonly string[]) =>
  index === 0 || index === 3 ? colors[0] : colors[1];

export type ChartWithBackgroundOptions = {
  chartTitle: string;
  chartUnits: string;
  chartData: NameValuePair[];
  chartIndex: number;
};

export const chartWithBackground = (
  options: ChartWithBackgroundOptions
): Slide => {
  const maxValue = Math.max(...options.chartData.map(data => data.value), 0);

  return {
    texts: [
      {
        text: chartTitleText(
          options.chartTitle,
          options.chartUnits,
          colorByIndex(options.chartIndex, titleColors)
        ),
        options: {
          fontSize: 10,
          color: theme.colors.white,
          align: 'center',
          h: 0.27,
          w: 2.9,
          x: 2.14 + applyChartXOffset(options.chartIndex),
          y: 1.63 + applyChartYOffset(options.chartIndex),
        },
      },
    ],
    shapes: [
      {
        shapeName: 'rect',
        options: {
          ...chartBackgroundOptions[options.chartIndex],
          h: 2.39,
          w: 5.87,
          x: 0.66 + applyChartXOffset(options.chartIndex),
          y: 1.57 + applyChartYOffset(options.chartIndex),
        },
      },
    ],
    charts: [
      {
        type: 'bar',
        data: [
          {
            name: 'Values',
            labels: options.chartData.map(data =>
              [
                data.value === maxValue ? '▶ ' : '',
                data.name.toUpperCase(),
              ].join('')
            ),
            values: options.chartData.map(data => data.value),
          },
        ],
        options: {
          ...defaultChartOptions,
          catAxisLabelFontSize: 6,
          barDir: 'bar',
          barGrouping: 'clustered',
          chartColors: options.chartData.map(data =>
            data.value === maxValue
              ? // Use slice(1) as the '#' character is not supported when multiple colors are provided for one series
                colorByIndex(options.chartIndex, highlightColors).slice(1)
              : colorByIndex(options.chartIndex, chartColors).slice(1)
          ),
          h: 2.15,
          w: 5.67,
          x: 0.79 + applyChartXOffset(options.chartIndex),
          y: 1.85 + applyChartYOffset(options.chartIndex),
        },
      },
    ],
  };
};
